/** Editor для Реалізації послуг з переробки (Порція 4b). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import {
  processingServicesSales,
  type ProcessingServicesSalesMaterialIn,
  type ProcessingServicesSalesProductIn,
  type ProcessingServicesSalesServiceIn,
  type ProcessingServicesSalesReturnedMaterialIn,
} from '@/api/processingServicesSales';
import { materialReceiptsForProcessing } from '@/api/materialReceiptsForProcessing';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import SearchSelect from '@/components/SearchSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { useConfirm } from '@/components/ConfirmProvider';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.processing.service.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  service_amount: string;
  vat_amount: string;
  revenue_account_code: string | null;
  receivable_account_code: string | null;
  comment: string;
  project_id: string | null;
  materials: ProcessingServicesSalesMaterialIn[];
  products: ProcessingServicesSalesProductIn[];
  services: ProcessingServicesSalesServiceIn[];
  returned_materials: ProcessingServicesSalesReturnedMaterialIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  service_amount: '0',
  vat_amount: '0',
  revenue_account_code: null,
  receivable_account_code: null,
  comment: '',
  project_id: null,
  materials: [],
  products: [],
  services: [],
  returned_materials: [],
};

const newM = (n: number): ProcessingServicesSalesMaterialIn => ({
  line_number: n, nomenclature_id: '', quantity: '0', declared_value: '0',
});
const newP = (n: number): ProcessingServicesSalesProductIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});
const newR = (n: number): ProcessingServicesSalesReturnedMaterialIn => ({
  line_number: n, nomenclature_id: '', quantity: '0', declared_value: '0',
});
const newS = (n: number): ProcessingServicesSalesServiceIn => ({
  line_number: n, nomenclature_id: null, description: '', quantity: '1',
  price: '0', amount: '0', vat_rate_percent: null, vat_amount: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProcessingServicesSalesEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // ч.692 (дзеркало processing-receipts): вкладки ТЧ + попап/швидке створення.
  const [tab, setTab] = useState<'products' | 'services' | 'materials' | 'returned' | 'accounts'>('products');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickNomOpen, setQuickNomOpen] = useState(false);
  // ч.693: «заповнити з приймання сировини» — попап вибору проведеної
  // приймання (022) цього замовника.
  const [receiptPickOpen, setReceiptPickOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.customer_contractor_id],
    queryFn: () => refsApi.contracts({
      organization_id: form.organization_id,
      contractor_id: form.customer_contractor_id,
    }),
    enabled: !!form.organization_id && !!form.customer_contractor_id,
  });
  const receiptsQ = useQuery({
    queryKey: ['mrfp-for-fill', form.organization_id, form.customer_contractor_id],
    queryFn: () => materialReceiptsForProcessing.list({
      organization_id: form.organization_id,
      customer_contractor_id: form.customer_contractor_id,
      posted: true, limit: 100,
    }),
    enabled: receiptPickOpen && !!form.organization_id && !!form.customer_contractor_id,
  });
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['processing-services-sales', id],
    queryFn: () => processingServicesSales.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.processing_services_sales', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        warehouse_id: d.warehouse_id,
        service_amount: d.service_amount,
        vat_amount: d.vat_amount,
        revenue_account_code: d.revenue_account_code ?? null,
        receivable_account_code: d.receivable_account_code ?? null,
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        materials: d.materials.map((m) => ({
          line_number: m.line_number, nomenclature_id: m.nomenclature_id, quantity: m.quantity,
          declared_value: m.declared_value ?? '0',
          warehouse_id: m.warehouse_id ?? null,
        })),
        products: d.products.map((p) => ({
          line_number: p.line_number, nomenclature_id: p.nomenclature_id, quantity: p.quantity,
        })),
        services: (d.services ?? []).map((sv) => ({
          line_number: sv.line_number, nomenclature_id: sv.nomenclature_id ?? null,
          description: sv.description ?? '', quantity: sv.quantity ?? '1',
          price: sv.price ?? '0', amount: sv.amount ?? '0',
          vat_rate_percent: sv.vat_rate_percent ?? null,
          vat_amount: sv.vat_amount ?? '0',
        })),
        returned_materials: (d.returned_materials ?? []).map((rm) => ({
          line_number: rm.line_number, nomenclature_id: rm.nomenclature_id,
          quantity: rm.quantity, declared_value: rm.declared_value ?? '0',
          warehouse_id: rm.warehouse_id ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctItems = acctList.map((a) => ({ id: a.code, label: `${a.code} — ${a.name}`, hint: null }));
  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <SearchSelect items={acctItems} value={value ?? ''}
      onChange={(v) => onChange(v || null)} disabled={isPosted}
      placeholder={`— (деф. ${def}) —`} />
  );

  const svcTotal = form.services.reduce((acc, sv) => acc + (Number(sv.amount) || 0), 0);
  const svcVatTotal = form.services.reduce((acc, sv) => acc + (Number(sv.vat_amount) || 0), 0);
  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    customer_contractor_id: form.customer_contractor_id,
    contract_id: form.contract_id || null,
    warehouse_id: form.warehouse_id,
    // ч.692: рядки послуг мають пріоритет — header синхронізується з Σ.
    service_amount: form.services.length > 0 ? svcTotal.toFixed(2) : form.service_amount,
    vat_amount: form.services.length > 0 ? svcVatTotal.toFixed(2) : form.vat_amount,
    services: form.services,
    returned_materials: form.returned_materials,
    revenue_account_code: form.revenue_account_code,
    receivable_account_code: form.receivable_account_code,
    comment: form.comment || null,
        project_id: form.project_id,
    materials: form.materials,
    products: form.products,
  });

  const create = useMutation({
    mutationFn: () => processingServicesSales.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales'] });
      navigate(`/processing-services-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => processingServicesSales.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales', id] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await processingServicesSales.create({ organization_id: form.organization_id, ...buildPayload() });
      try {
        await processingServicesSales.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/processing-services-sales/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales'] });
      navigate(`/processing-services-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await processingServicesSales.update(id!, buildPayload());
      await processingServicesSales.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-services-sales'] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => processingServicesSales.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-services-sales'] }),
    onError: (e) => setError(extractError(e)),
  });
  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => processingServicesSales.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales'] });
      navigate('/processing-services-sales');
    },
    onError: (e) => setError(extractError(e)),
  });

  function rmM(i: number) {
    setForm((f) => ({ ...f, materials: f.materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setM(i: number, patch: Partial<ProcessingServicesSalesMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmP(i: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setP(i: number, patch: Partial<ProcessingServicesSalesProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmS(i: number) {
    setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setS(i: number, patch: Partial<ProcessingServicesSalesServiceIn>) {
    setForm((f) => ({ ...f, services: f.services.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function rmR(i: number) {
    setForm((f) => ({ ...f, returned_materials: f.returned_materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setR(i: number, patch: Partial<ProcessingServicesSalesReturnedMaterialIn>) {
    setForm((f) => ({ ...f, returned_materials: f.returned_materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  // ч.693: заповнити ТЧ активної вкладки рядками ПРОВЕДЕНОЇ приймання
  // давальницької сировини (022) цього замовника.
  async function fillFromReceipt(receiptId: string) {
    setError(null);
    try {
      const r = await materialReceiptsForProcessing.get(receiptId);
      const rows = r.items.map((it, idx) => ({
        line_number: idx + 1,
        nomenclature_id: it.nomenclature_id,
        quantity: it.quantity,
        declared_value: it.declared_value ?? '0',
        warehouse_id: it.warehouse_id ?? r.destination_warehouse_id ?? null,
      }));
      setForm((f) => (tab === 'returned'
        ? { ...f, returned_materials: rows }
        : { ...f, materials: rows }));
      setReceiptPickOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }
  // ч.692: рядки з попапа мультивибору — у ТЧ активної вкладки.
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => {
      if (tab === 'products') {
        return { ...f, products: [...f.products, ...rows.map((r, k) => ({
          ...newP(f.products.length + 1 + k), nomenclature_id: r.nomenclature_id,
          quantity: '1',
        }))] };
      }
      if (tab === 'services') {
        return { ...f, services: [...f.services, ...rows.map((r, k) => ({
          ...newS(f.services.length + 1 + k), nomenclature_id: r.nomenclature_id,
        }))] };
      }
      if (tab === 'returned') {
        return { ...f, returned_materials: [...f.returned_materials, ...rows.map((r, k) => ({
          ...newR(f.returned_materials.length + 1 + k), nomenclature_id: r.nomenclature_id,
          quantity: '1',
        }))] };
      }
      return { ...f, materials: [...f.materials, ...rows.map((r, k) => ({
        ...newM(f.materials.length + 1 + k), nomenclature_id: r.nomenclature_id,
        quantity: '1',
      }))] };
    });
  }
  // ч.911 (Закон #10): кнопки ТЧ — у шапці блоку (діють на активну вкладку;
  // на вкладці «Рахунки обліку» їх не було — так і лишилось).
  const tableActions = !isPosted && tab !== 'accounts' ? (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      {(tab === 'materials' || tab === 'returned') && (
        <button type="button" className="btn-doc" onClick={() => setReceiptPickOpen(true)}
          disabled={!form.customer_contractor_id}
          title={!form.customer_contractor_id
            ? 'Спершу оберіть замовника'
            : 'Замінити рядки вкладки позиціями проведеної приймання давальницької сировини (022) цього замовника'}>
          заповнити з приймання сировини
        </button>
      )}
      <button type="button" className="btn-doc" onClick={() => setQuickNomOpen(true)}
        title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
        створити номенклатуру
      </button>
      <button type="button" className="primary" onClick={() => setPickerOpen(true)}
        disabled={!form.organization_id}
        title={!form.organization_id ? 'Спершу оберіть юрособу'
          : 'Підбір з довідника номенклатури (мультивибір)'}>
        + додати номенклатуру
      </button>
    </div>
  ) : undefined;

  // ч.695: ПН з реалізації послуг переробки (base_document_*).
  const [pnBusy, setPnBusy] = useState(false);
  const pnListQ = useQuery({
    queryKey: ['pn-for-pss', id],
    queryFn: () => taxInvoicesOutgoing.list({ limit: 500 }),
    enabled: !isNew && isPosted,
  });
  const existingPn = useMemo(() => {
    return (pnListQ.data?.items ?? [])
      .find((p) => p.base_document_id === id && !p.deletion_mark) ?? null;
  }, [pnListQ.data, id]);
  async function createPnFromPss() {
    if (!id) return;
    setPnBusy(true);
    try {
      const pn = await taxInvoicesOutgoing.createFromSale({
        organization_id: form.organization_id,
        base_document_type: 'doc.processing_services_sales',
        base_document_id: id,
      });
      navigate(`/tax-invoices-outgoing/${pn.id}`);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setPnBusy(false);
    }
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  // ч.911 (новий дизайн, Закон #10): «ще дії» → права колонка «Повʼязані дії»
  // (обидві гілки ПН збережено дослівно).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [
    existingPn ? {
      label: 'відкрити ПН',
      title: `ПН №${existingPn.invoice_number} вже створена з цього документа`,
      onClick: () => navigate(`/tax-invoices-outgoing/${existingPn.id}`),
    } : {
      label: pnBusy ? 'ПН створюється…' : 'створити ПН',
      title: isNew ? 'Спершу збережіть документ'
        : 'Створити податкову накладну на послуги переробки (рядки вкладки «Послуги»)',
      disabled: pnBusy || isNew,
      onClick: () => void createPnFromPss(),
    },
  ];
  // ч.911: прогрес — ЛИШЕ обовʼязкові поля.
  const rowsCount = form.products.length + form.services.length
    + form.materials.length + form.returned_materials.length;
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Замовник, договір і склад', required: 2,
      filled: (form.customer_contractor_id ? 1 : 0) + (form.warehouse_id ? 1 : 0) },
    { id: 'items', title: 'Продукція, послуги та сировина', required: 1,
      filled: rowsCount > 0 || Number(form.service_amount) > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.911 (новий дизайн, Закон #10): блоки + права рейка. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова реалізація послуги переробки' : `Реалізація: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.processing_services_sales', id: isNew ? '' : id! }}
          onBackToList={() => navigate('/processing-services-sales')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/processing-services-sales/${id}/print`)}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.processing_services_sales', id: isNew ? '' : id! }} />
      }>
      {/* ч.692 (дзеркало processing-receipts): [Юрособа*|Номер|Дата] →
          [Замовник*|Договір(+створити)|Склад*]. ч.911 — блоками; коментар —
          в останньому блоці. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })} disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <ProjectField span={2}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Замовник, договір і склад">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Замовник *</label>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={form.customer_contractor_id}
            onChange={(v) => setForm({ ...form, customer_contractor_id: v, contract_id: '' })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            Договір{' '}
            <a href={`/refs/contracts/new${form.customer_contractor_id ? `?contractor_id=${form.customer_contractor_id}` : ''}`}
               target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', fontWeight: 'normal',
               color: 'var(--primary)', marginLeft: 6 }}>створити ↗</a>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.customer_contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Склад *</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.name} — {w.code}</option>))}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Продукція, послуги та сировина" actions={tableActions}>
      {/* Вкладки ТЧ — канон seg-tabs, «Рахунки обліку» справа. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={tab === 'products' ? 'active' : ''}
            onClick={() => setTab('products')}>Продукція замовника ({form.products.length})</button>
          <button type="button" className={tab === 'services' ? 'active' : ''}
            onClick={() => setTab('services')}>Послуги ({form.services.length})</button>
          <button type="button" className={tab === 'materials' ? 'active' : ''}
            onClick={() => setTab('materials')}>Використана сировина ({form.materials.length})</button>
          <button type="button" className={tab === 'returned' ? 'active' : ''}
            onClick={() => setTab('returned')}>Повернена сировина ({form.returned_materials.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={tab === 'accounts' ? 'active' : ''}
            onClick={() => setTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>

      {tab === 'accounts' && (
        <div className="doc-grid" style={{ marginTop: '0.75rem' }}>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рахунок доходів</label>
            {acctSelect(form.revenue_account_code, (v) => setForm({ ...form, revenue_account_code: v }), '703')}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рахунок розрахунків</label>
            {acctSelect(form.receivable_account_code, (v) => setForm({ ...form, receivable_account_code: v }), '361')}
          </div>
        </div>
      )}

      {tab === 'products' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Продукція (повертаємо замовнику)</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.products.map((p, i) => (
                <tr key={i}>
                  <td>{p.line_number}</td>
                  <td>
                    <NomenclatureSelect value={p.nomenclature_id}
                      onChange={(v) => setP(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <input type="number" step="0.001" value={p.quantity}
                           onChange={(e) => setP(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmP(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}

      {tab === 'services' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 220 }}>Номенклатура (послуга)</th>
                <th>Зміст</th>
                <th style={{ width: 90 }}>К-сть</th>
                <th style={{ width: 100 }}>Ціна</th>
                <th style={{ width: 110 }}>Сума</th>
                <th style={{ width: 90 }}>Ставка ПДВ</th>
                <th style={{ width: 100 }}>ПДВ</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.services.map((sv, i) => (
                <tr key={i}>
                  <td>{sv.line_number}</td>
                  <td>
                    <NomenclatureSelect value={sv.nomenclature_id ?? ''}
                      onChange={(v) => setS(i, { nomenclature_id: v || null })}
                      disabled={isPosted} kind="services" />
                  </td>
                  <td>
                    <input value={sv.description ?? ''}
                           onChange={(e) => setS(i, { description: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.001" value={sv.quantity ?? '1'}
                           onChange={(e) => {
                             const q = e.target.value;
                             const amt = (Number(q) || 0) * (Number(sv.price) || 0);
                             setS(i, { quantity: q, amount: amt ? amt.toFixed(2) : sv.amount });
                           }} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.price ?? ''}
                           onChange={(e) => {
                             const pr = e.target.value;
                             const amt = (Number(pr) || 0) * (Number(sv.quantity) || 0);
                             setS(i, { price: pr || null, amount: amt ? amt.toFixed(2) : sv.amount });
                           }} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.amount ?? '0'}
                           onChange={(e) => {
                             const amt = e.target.value;
                             const rate = Number(sv.vat_rate_percent);
                             setS(i, {
                               amount: amt,
                               ...(sv.vat_rate_percent != null && sv.vat_rate_percent !== ''
                                 ? { vat_amount: ((Number(amt) || 0) * rate / 100).toFixed(2) }
                                 : {}),
                             });
                           }}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>
                    <select value={sv.vat_rate_percent ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setS(i, {
                                vat_rate_percent: v || null,
                                ...(v !== ''
                                  ? { vat_amount: ((Number(sv.amount) || 0) * Number(v) / 100).toFixed(2) }
                                  : {}),
                              });
                            }}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      <option value="20">20%</option>
                      <option value="14">14%</option>
                      <option value="7">7%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.01" value={sv.vat_amount ?? '0'}
                           onChange={(e) => setS(i, { vat_amount: e.target.value })}
                           disabled={isPosted} style={{ textAlign: 'right' }} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmS(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
            {form.services.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td colSpan={5} style={{ textAlign: 'right' }}>Разом послуг:</td>
                  <td style={{ textAlign: 'right' }}>{svcTotal.toFixed(2)}</td>
                  <td />
                  <td style={{ textAlign: 'right' }}>{svcVatTotal.toFixed(2)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </TchTable>
          {form.services.length === 0 && (
            <div className="doc-grid" style={{ marginTop: '0.75rem' }}>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>Сума послуги (без рядків)</label>
                <input type="number" step="0.01" value={form.service_amount}
                       onChange={(e) => setForm({ ...form, service_amount: e.target.value })} disabled={isPosted} />
              </div>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>ПДВ</label>
                <input type="number" step="0.01" value={form.vat_amount}
                       onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} disabled={isPosted} />
              </div>
            </div>
          )}
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Наш дохід за переробку: Дт 361 — Кт 703 (+ ПДВ-зобовʼязання).
            Рядки мають пріоритет; без рядків діють поля сум.
          </p>
        </div>
      )}

      {tab === 'materials' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Сировина замовника (списання з 022)</th>
                <th style={{ width: 170 }}>Склад зберігання</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 130 }}>Декл. вартість</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.materials.map((m, i) => (
                <tr key={i}>
                  <td>{m.line_number}</td>
                  <td>
                    <NomenclatureSelect value={m.nomenclature_id}
                      onChange={(v) => setM(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <select value={m.warehouse_id ?? ''}
                            onChange={(e) => setM(i, { warehouse_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      {warehousesQ.data?.map((w) => (
                        <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.001" value={m.quantity}
                           onChange={(e) => setM(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={m.declared_value ?? '0'}
                           onChange={(e) => setM(i, { declared_value: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmM(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Давальницька сировина — на позабалансі 022 (без рухів складу):
            у складських залишках і підборі реалізації її НЕМАЄ — випадково
            продати чуже неможливо. Склад тут — місце зберігання.
          </p>
        </div>
      )}

      {tab === 'returned' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Сировина (повертаємо замовнику невикористану)</th>
                <th style={{ width: 170 }}>Склад зберігання</th>
                <th style={{ width: 100 }}>К-сть</th>
                <th style={{ width: 130 }}>Декл. вартість</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.returned_materials.map((rm, i) => (
                <tr key={i}>
                  <td>{rm.line_number}</td>
                  <td>
                    <NomenclatureSelect value={rm.nomenclature_id}
                      onChange={(v) => setR(i, { nomenclature_id: v })} disabled={isPosted} kind="goods" />
                  </td>
                  <td>
                    <select value={rm.warehouse_id ?? ''}
                            onChange={(e) => setR(i, { warehouse_id: e.target.value || null })}
                            disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      {warehousesQ.data?.map((w) => (
                        <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.001" value={rm.quantity}
                           onChange={(e) => setR(i, { quantity: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>
                    <input type="number" step="0.01" value={rm.declared_value ?? '0'}
                           onChange={(e) => setR(i, { declared_value: e.target.value })} disabled={isPosted} />
                  </td>
                  <td>{!isPosted && <button onClick={() => rmR(i)}>×</button>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Невикористана сировина повертається замовнику: списується з 022
            за декларованою оцінкою (знімається матвідповідальність).
          </p>
        </div>
      )}

      {/* ч.692: попап підбору (мультивибір; послуги — з довідника послуг). */}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          catalogMode
          onlyKind={tab === 'services' ? 'services' : 'goods'}
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            addRowsFromPicker([{
              warehouse_id: '', warehouse_code: '', warehouse_name: '',
              nomenclature_id: nom.id, nomenclature_code: nom.code,
              nomenclature_article: null, nomenclature_name: nom.name,
              quantity: '0', cost: null,
            } as GoodsBalanceRow]);
          }}
        />
      )}

      {/* ч.693: вибір проведеної приймання сировини для заповнення ТЧ. */}
      {receiptPickOpen && (
        <div onClick={() => setReceiptPickOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 620,
              maxHeight: '70vh', overflow: 'auto', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Приймання давальницької сировини</h3>
              <button onClick={() => setReceiptPickOpen(false)}
                style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem' }}>×</button>
            </div>
            {(receiptsQ.data?.items ?? []).length === 0 ? (
              <p style={{ color: 'var(--muted)' }}>
                Немає проведених приймань сировини цього замовника.
              </p>
            ) : (
              <TchTable>
                <thead>
                  <tr>
                    <th>Номер</th>
                    <th style={{ width: 110 }}>Дата</th>
                    <th style={{ width: 140 }}>Задекл. вартість</th>
                  </tr>
                </thead>
                <tbody>
                  {(receiptsQ.data?.items ?? []).map((r) => (
                    <tr key={r.id} onClick={() => fillFromReceipt(r.id)}
                        style={{ cursor: 'pointer' }}
                        title="Замінити рядки вкладки позиціями цієї приймання">
                      <td>{r.document_number}</td>
                      <td>{r.document_date.slice(0, 10)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {r.total_declared_value ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.processing_services_sales" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
